import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Phone, ArrowRight, Building2, Users, Clock, Shield, Star, Navigation, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';
import { cities, services } from '../data/seoPages';
import SEOHead from '../components/seo/SEOHead';

// City data with coordinates and details for LocalBusiness Schema
const cityDetails = {
  'miami': { 
    lat: 25.7617, lng: -80.1918, 
    population: '450,000+',
    areaCode: '305/786',
    description: 'Miami is South Florida\'s economic hub, home to major corporations, healthcare systems, and a thriving tech sector.',
    description_es: 'Miami es el centro económico del sur de Florida, hogar de grandes corporaciones, sistemas de salud y un próspero sector tecnológico.',
    industries: ['Healthcare', 'Finance', 'Hospitality', 'Technology', 'Real Estate'],
    zipCodes: ['33101', '33109', '33125', '33126', '33127', '33128', '33129', '33130', '33131', '33132', '33133', '33134', '33135', '33136', '33137', '33138', '33139', '33140', '33141', '33142', '33143', '33144', '33145', '33146', '33147', '33149', '33150', '33155', '33156', '33157', '33158', '33161', '33162', '33165', '33166', '33167', '33168', '33169', '33170', '33172', '33173', '33174', '33175', '33176', '33177', '33178', '33179', '33180', '33181', '33182', '33183', '33184', '33185', '33186', '33187', '33189', '33190', '33193', '33194', '33196']
  },
  'orlando': { 
    lat: 28.5383, lng: -81.3792, 
    population: '310,000+',
    areaCode: '407/321',
    description: 'Orlando is Central Florida\'s largest city, famous for theme parks but also a major hub for technology, aerospace, and simulation industries.',
    description_es: 'Orlando es la ciudad más grande del centro de Florida, famosa por sus parques temáticos pero también un importante centro de tecnología, aeroespacial e industrias de simulación.',
    industries: ['Tourism', 'Technology', 'Aerospace', 'Healthcare', 'Education'],
    zipCodes: ['32801', '32803', '32804', '32805', '32806', '32807', '32808', '32809', '32810', '32811', '32812', '32814', '32817', '32818', '32819', '32820', '32821', '32822', '32824', '32825', '32826', '32827', '32828', '32829', '32830', '32831', '32832', '32833', '32835', '32836', '32837', '32839']
  },
  'tampa': { 
    lat: 27.9506, lng: -82.4572, 
    population: '400,000+',
    areaCode: '813',
    description: 'Tampa is a major business center on Florida\'s Gulf Coast with strong healthcare, finance, and technology sectors.',
    description_es: 'Tampa es un importante centro de negocios en la costa del Golfo de Florida con fuertes sectores de salud, finanzas y tecnología.',
    industries: ['Healthcare', 'Finance', 'Technology', 'Manufacturing', 'Port/Logistics'],
    zipCodes: ['33601', '33602', '33603', '33604', '33605', '33606', '33607', '33609', '33610', '33611', '33612', '33613', '33614', '33615', '33616', '33617', '33618', '33619', '33620', '33621', '33624', '33625', '33626', '33629', '33634', '33635', '33647']
  },
  'fort-lauderdale': { 
    lat: 26.1224, lng: -80.1373, 
    population: '185,000+',
    areaCode: '954',
    description: 'Fort Lauderdale is a major cruise port and yachting capital with thriving marine, tourism, and technology industries.',
    description_es: 'Fort Lauderdale es un importante puerto de cruceros y capital del yate con prósperas industrias marinas, turísticas y tecnológicas.',
    industries: ['Marine', 'Tourism', 'Technology', 'Healthcare', 'Finance'],
    zipCodes: ['33301', '33304', '33305', '33306', '33308', '33309', '33311', '33312', '33313', '33314', '33315', '33316', '33317', '33319', '33321', '33322', '33323', '33324', '33325', '33326', '33327', '33328', '33330', '33331', '33332', '33334', '33351']
  },
  'jacksonville': { 
    lat: 30.3322, lng: -81.6557, 
    population: '950,000+',
    areaCode: '904',
    description: 'Jacksonville is Florida\'s largest city by area and a major port city with strong logistics, finance, and healthcare sectors.',
    description_es: 'Jacksonville es la ciudad más grande de Florida por área y un importante puerto con fuertes sectores de logística, finanzas y salud.',
    industries: ['Logistics', 'Finance', 'Healthcare', 'Insurance', 'Manufacturing'],
    zipCodes: ['32099', '32201', '32202', '32204', '32205', '32206', '32207', '32208', '32209', '32210', '32211', '32212', '32214', '32216', '32217', '32218', '32219', '32220', '32221', '32222', '32223', '32224', '32225', '32226', '32227', '32228', '32233', '32234', '32244', '32246', '32250', '32254', '32256', '32257', '32258', '32266', '32277']
  },
  'west-palm-beach': { 
    lat: 26.7153, lng: -80.0534, 
    population: '115,000+',
    areaCode: '561',
    description: 'West Palm Beach is Palm Beach County\'s largest city, known for luxury real estate and growing business district.',
    description_es: 'West Palm Beach es la ciudad más grande del condado de Palm Beach, conocida por bienes raíces de lujo y un creciente distrito comercial.',
    industries: ['Real Estate', 'Finance', 'Healthcare', 'Retail', 'Technology'],
    zipCodes: ['33401', '33402', '33403', '33404', '33405', '33406', '33407', '33409', '33410', '33411', '33412', '33413', '33414', '33415', '33416', '33417', '33418', '33419']
  },
  'tallahassee': { 
    lat: 30.4383, lng: -84.2807, 
    population: '195,000+',
    areaCode: '850',
    description: 'Tallahassee is Florida\'s capital city, home to state government, two major universities, and growing tech sector.',
    description_es: 'Tallahassee es la capital de Florida, hogar del gobierno estatal, dos universidades importantes y un creciente sector tecnológico.',
    industries: ['Government', 'Education', 'Healthcare', 'Technology', 'Research'],
    zipCodes: ['32301', '32302', '32303', '32304', '32305', '32306', '32307', '32308', '32309', '32310', '32311', '32312', '32313', '32314', '32315', '32316', '32317', '32318']
  },
  'panama-city': { 
    lat: 30.1588, lng: -85.6602, 
    population: '35,000+',
    areaCode: '850',
    description: 'Panama City is the Panhandle\'s largest city, with military bases, tourism, and growing commercial sectors.',
    description_es: 'Panama City es la ciudad más grande del Panhandle, con bases militares, turismo y sectores comerciales en crecimiento.',
    industries: ['Military', 'Tourism', 'Healthcare', 'Retail', 'Manufacturing'],
    zipCodes: ['32401', '32402', '32403', '32404', '32405', '32406', '32407', '32408', '32409', '32410', '32411', '32412', '32413']
  },
  'fort-myers': { 
    lat: 26.6406, lng: -81.8723, 
    population: '95,000+',
    areaCode: '239',
    description: 'Fort Myers is Southwest Florida\'s commercial hub with strong healthcare, retail, and real estate industries.',
    description_es: 'Fort Myers es el centro comercial del suroeste de Florida con fuertes industrias de salud, retail e inmobiliarias.',
    industries: ['Healthcare', 'Real Estate', 'Retail', 'Tourism', 'Construction'],
    zipCodes: ['33901', '33902', '33903', '33904', '33905', '33906', '33907', '33908', '33909', '33912', '33913', '33916', '33917', '33919']
  },
  'winter-haven': { 
    lat: 28.0222, lng: -81.7329, 
    population: '50,000+',
    areaCode: '863',
    description: 'Winter Haven is Polk County\'s second-largest city, known for agriculture, logistics, and proximity to LEGOLAND.',
    description_es: 'Winter Haven es la segunda ciudad más grande del condado de Polk, conocida por agricultura, logística y proximidad a LEGOLAND.',
    industries: ['Agriculture', 'Logistics', 'Tourism', 'Healthcare', 'Retail'],
    zipCodes: ['33880', '33881', '33882', '33883', '33884', '33885']
  },
  'naples': { 
    lat: 26.1420, lng: -81.7948, 
    population: '22,000+',
    areaCode: '239',
    description: 'Naples is one of Florida\'s wealthiest cities with luxury real estate, healthcare, and high-end retail.',
    description_es: 'Naples es una de las ciudades más ricas de Florida con bienes raíces de lujo, salud y retail de alta gama.',
    industries: ['Real Estate', 'Healthcare', 'Retail', 'Finance', 'Tourism'],
    zipCodes: ['34101', '34102', '34103', '34104', '34105', '34106', '34107', '34108', '34109', '34110', '34112', '34113', '34114', '34116', '34117', '34119', '34120']
  },
  'sarasota': { 
    lat: 27.3364, lng: -82.5307, 
    population: '57,000+',
    areaCode: '941',
    description: 'Sarasota is known for arts, culture, and beaches, with growing healthcare and technology sectors.',
    description_es: 'Sarasota es conocida por arte, cultura y playas, con crecientes sectores de salud y tecnología.',
    industries: ['Healthcare', 'Arts/Culture', 'Tourism', 'Real Estate', 'Technology'],
    zipCodes: ['34230', '34231', '34232', '34233', '34234', '34235', '34236', '34237', '34238', '34239', '34240', '34241', '34242', '34243']
  },
  'gainesville': { 
    lat: 29.6516, lng: -82.3248, 
    population: '140,000+',
    areaCode: '352',
    description: 'Gainesville is home to the University of Florida and a major healthcare and research hub.',
    description_es: 'Gainesville es hogar de la Universidad de Florida y un importante centro de salud e investigación.',
    industries: ['Education', 'Healthcare', 'Research', 'Technology', 'Biotech'],
    zipCodes: ['32601', '32602', '32603', '32604', '32605', '32606', '32607', '32608', '32609', '32610', '32611', '32612', '32614', '32627', '32635', '32641']
  },
  'ocala': { 
    lat: 29.1872, lng: -82.1401, 
    population: '65,000+',
    areaCode: '352',
    description: 'Ocala is the heart of Florida\'s horse country with agriculture, healthcare, and logistics industries.',
    description_es: 'Ocala es el corazón del país de los caballos de Florida con industrias de agricultura, salud y logística.',
    industries: ['Agriculture', 'Healthcare', 'Logistics', 'Manufacturing', 'Retail'],
    zipCodes: ['34470', '34471', '34472', '34473', '34474', '34475', '34476', '34477', '34478', '34479', '34480', '34481', '34482', '34483']
  },
  'daytona-beach': { 
    lat: 29.2108, lng: -81.0228, 
    population: '70,000+',
    areaCode: '386',
    description: 'Daytona Beach is famous for NASCAR and motorsports, with tourism, healthcare, and aviation industries.',
    description_es: 'Daytona Beach es famosa por NASCAR y deportes de motor, con industrias de turismo, salud y aviación.',
    industries: ['Motorsports', 'Tourism', 'Healthcare', 'Aviation', 'Education'],
    zipCodes: ['32114', '32115', '32116', '32117', '32118', '32119', '32120', '32121', '32122', '32123', '32124', '32125', '32126', '32127', '32128', '32129']
  },
  'boca-raton': { 
    lat: 26.3683, lng: -80.1289, 
    population: '100,000+',
    areaCode: '561',
    description: 'Boca Raton is an affluent city with major corporate headquarters, technology companies, and Florida Atlantic University.',
    description_es: 'Boca Raton es una ciudad próspera con sedes corporativas importantes, empresas de tecnología y la Universidad Atlántica de Florida.',
    industries: ['Technology', 'Finance', 'Healthcare', 'Education', 'Corporate HQs'],
    zipCodes: ['33427', '33428', '33429', '33431', '33432', '33433', '33434', '33464', '33481', '33486', '33487', '33488', '33496', '33497', '33498', '33499']
  },
  'coral-springs': { 
    lat: 26.2712, lng: -80.2706, 
    population: '135,000+',
    areaCode: '954',
    description: 'Coral Springs is a planned community known for excellent schools and family-friendly environment.',
    description_es: 'Coral Springs es una comunidad planificada conocida por excelentes escuelas y ambiente familiar.',
    industries: ['Retail', 'Healthcare', 'Education', 'Finance', 'Technology'],
    zipCodes: ['33065', '33067', '33071', '33073', '33075', '33076', '33077']
  },
  'pompano-beach': { 
    lat: 26.2379, lng: -80.1248, 
    population: '115,000+',
    areaCode: '954',
    description: 'Pompano Beach has a diverse economy with manufacturing, retail, and growing technology sectors.',
    description_es: 'Pompano Beach tiene una economía diversa con manufactura, retail y crecientes sectores tecnológicos.',
    industries: ['Manufacturing', 'Retail', 'Technology', 'Healthcare', 'Marine'],
    zipCodes: ['33060', '33061', '33062', '33063', '33064', '33066', '33068', '33069', '33071', '33072', '33073', '33074', '33075', '33076', '33077']
  },
  'hollywood': { 
    lat: 26.0112, lng: -80.1495, 
    population: '155,000+',
    areaCode: '954',
    description: 'Hollywood is strategically located between Miami and Fort Lauderdale with diverse commercial activities.',
    description_es: 'Hollywood está estratégicamente ubicado entre Miami y Fort Lauderdale con diversas actividades comerciales.',
    industries: ['Healthcare', 'Retail', 'Tourism', 'Technology', 'Manufacturing'],
    zipCodes: ['33019', '33020', '33021', '33022', '33023', '33024', '33025', '33026', '33027', '33028', '33029', '33081', '33083', '33084']
  },
  'hialeah': { 
    lat: 25.8576, lng: -80.2781, 
    population: '225,000+',
    areaCode: '305/786',
    description: 'Hialeah is Florida\'s sixth-largest city with strong manufacturing, healthcare, and retail sectors.',
    description_es: 'Hialeah es la sexta ciudad más grande de Florida con fuertes sectores de manufactura, salud y retail.',
    industries: ['Manufacturing', 'Healthcare', 'Retail', 'Logistics', 'Construction'],
    zipCodes: ['33002', '33010', '33011', '33012', '33013', '33014', '33015', '33016', '33017', '33018']
  }
};

export default function Locations() {
  const { language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter cities by search
  const filteredCities = cities.filter(city => 
    city.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    city.region.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group cities by region
  const citiesByRegion = filteredCities.reduce((acc, city) => {
    if (!acc[city.region]) acc[city.region] = [];
    acc[city.region].push(city);
    return acc;
  }, {});

  // Generate LocalBusiness Schema for all locations
  useEffect(() => {
    // Remove existing schemas
    document.querySelectorAll('script[data-location-schema]').forEach(el => el.remove());
    
    // Create LocalBusiness schema for each city
    cities.forEach(city => {
      const details = cityDetails[city.slug] || {};
      const schema = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": `https://www.iqeslowvoltage.com/locations#${city.slug}`,
        "name": `IQES Low Voltage Solutions - ${city.name}`,
        "description": `Professional low voltage contractor serving ${city.name}, FL. CCTV, fiber optic, structured cabling, access control. Licensed & insured.`,
        "url": `https://www.iqeslowvoltage.com/cctv-installation-${city.slug}`,
        "telephone": "+1-786-981-3760",
        "email": "info@iqeslowvoltage.com",
        "image": "https://www.iqeslowvoltage.com/logo-iqes-new.png",
        "priceRange": "$$",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": city.name,
          "addressRegion": "FL",
          "addressCountry": "US"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": details.lat || 27.6648,
          "longitude": details.lng || -81.5158
        },
        "areaServed": {
          "@type": "City",
          "name": city.name,
          "containedInPlace": {
            "@type": "State",
            "name": "Florida"
          }
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "08:00",
            "closes": "18:00"
          }
        ],
        "sameAs": [
          "https://www.facebook.com/iqeslowvoltage",
          "https://www.instagram.com/iqeslowvoltage"
        ]
      };
      
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-location-schema', city.slug);
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
    
    return () => {
      document.querySelectorAll('script[data-location-schema]').forEach(el => el.remove());
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="locations-page">
      {/* SEO Head */}
      <SEOHead 
        title="Service Locations in Florida"
        description="IQES Low Voltage serves 20+ Florida cities: Miami, Orlando, Tampa, Fort Lauderdale, Jacksonville & more. CCTV, fiber optic, cabling. Call 786-981-3760"
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#003366] via-[#004488] to-[#002244] text-white pt-28 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full mb-6">
              <MapPin className="w-5 h-5 text-orange-400" />
              <span className="text-sm font-medium">Serving All of Florida</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {language === 'es' ? 'Áreas de Servicio en Florida' : 'Service Locations in Florida'}
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              {language === 'es' 
                ? 'Servimos más de 20 ciudades en Florida con instalaciones profesionales de bajo voltaje. Técnicos locales, respuesta rápida.'
                : 'Serving 20+ Florida cities with professional low voltage installations. Local technicians, fast response times.'}
            </p>
            
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <div className="text-3xl font-bold text-orange-400">20+</div>
                <div className="text-sm text-gray-300">{language === 'es' ? 'Ciudades' : 'Cities'}</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <div className="text-3xl font-bold text-orange-400">13+</div>
                <div className="text-sm text-gray-300">{language === 'es' ? 'Años' : 'Years'}</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <div className="text-3xl font-bold text-orange-400">500+</div>
                <div className="text-sm text-gray-300">{language === 'es' ? 'Proyectos' : 'Projects'}</div>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-xl p-4">
                <div className="text-3xl font-bold text-orange-400">24/7</div>
                <div className="text-sm text-gray-300">{language === 'es' ? 'Soporte' : 'Support'}</div>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative max-w-md mx-auto">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder={language === 'es' ? 'Buscar ciudad...' : 'Search city...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-800 focus:ring-2 focus:ring-orange-500"
                data-testid="location-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Florida Map Section */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="md:w-1/2">
              <img 
                src="/florida-map-coverage.png" 
                alt="IQES Low Voltage service coverage map of Florida showing Miami, Orlando, Tampa, Jacksonville and other cities"
                className="w-full max-w-md mx-auto"
              />
            </div>
            <div className="md:w-1/2">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
                {language === 'es' ? 'Cobertura en Todo Florida' : 'Statewide Florida Coverage'}
              </h2>
              <p className="text-gray-600 mb-6">
                {language === 'es'
                  ? 'IQES tiene técnicos locales en las principales áreas metropolitanas de Florida. Esto significa tiempos de respuesta más rápidos, conocimiento de códigos locales y precios competitivos para su mercado.'
                  : 'IQES has local technicians in Florida\'s major metropolitan areas. This means faster response times, knowledge of local building codes, and competitive pricing for your market.'}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-orange-500" />
                  <span className="text-sm text-gray-700">{language === 'es' ? 'Respuesta en 24h' : '24h Response Time'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-orange-500" />
                  <span className="text-sm text-gray-700">{language === 'es' ? 'Licenciado y Asegurado' : 'Licensed & Insured'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-orange-500" />
                  <span className="text-sm text-gray-700">{language === 'es' ? 'Técnicos Locales' : 'Local Technicians'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-orange-500" />
                  <span className="text-sm text-gray-700">{language === 'es' ? 'Cotizaciones Gratis' : 'Free Estimates'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cities by Region */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-12">
            {language === 'es' ? 'Ciudades por Región' : 'Cities by Region'}
          </h2>
          
          {Object.keys(citiesByRegion).sort().map((region) => (
            <div key={region} className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <Navigation className="w-6 h-6 text-orange-500" />
                <h3 className="text-xl font-bold text-gray-800">{region}</h3>
                <span className="text-sm text-gray-500">({citiesByRegion[region].length} {language === 'es' ? 'ciudades' : 'cities'})</span>
              </div>
              
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {citiesByRegion[region].map((city, index) => {
                  const details = cityDetails[city.slug] || {};
                  return (
                    <motion.div
                      key={city.slug}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card className="h-full hover:shadow-lg transition-all border-gray-200 hover:border-orange-300 group">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <h4 className="font-bold text-gray-800 group-hover:text-orange-600 transition-colors">
                                {city.name}
                              </h4>
                              <p className="text-sm text-gray-500">{city.state}</p>
                            </div>
                            <MapPin className="w-5 h-5 text-orange-500" />
                          </div>
                          
                          {details.population && (
                            <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                              <Users className="w-4 h-4" />
                              <span>{details.population} residents</span>
                            </div>
                          )}
                          
                          {details.industries && (
                            <div className="flex flex-wrap gap-1 mb-3">
                              {details.industries.slice(0, 3).map((ind, i) => (
                                <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                  {ind}
                                </span>
                              ))}
                            </div>
                          )}
                          
                          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                            {services.slice(0, 3).map((service) => (
                              <Link
                                key={service.slug}
                                to={`/${service.slug}-${city.slug}`}
                                className="text-xs text-orange-600 hover:text-orange-700 hover:underline"
                                title={`${service.name} in ${city.name}`}
                              >
                                {service.shortName}
                              </Link>
                            ))}
                            <Link
                              to={`/cctv-installation-${city.slug}`}
                              className="text-xs text-gray-400 hover:text-orange-500"
                            >
                              +more
                            </Link>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-gray-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
              {language === 'es' ? 'Servicios Disponibles en Todas las Ubicaciones' : 'Services Available at All Locations'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {language === 'es'
                ? 'Ofrecemos la gama completa de servicios de bajo voltaje en todas nuestras áreas de servicio de Florida.'
                : 'We offer the full range of low voltage services across all our Florida service areas.'}
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {services.map((service, index) => (
              <motion.div
                key={service.slug}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link 
                  to={`/${service.slug}-miami`}
                  className="block bg-white rounded-xl p-5 text-center hover:shadow-lg transition-all group border border-gray-200 hover:border-orange-300"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-orange-500 transition-colors">
                    <Wrench className="w-6 h-6 text-orange-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-semibold text-gray-800 group-hover:text-orange-600 transition-colors">
                    {service.shortName}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">20+ locations</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SEO Content Block */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
              {language === 'es' ? 'Contratista de Bajo Voltaje en Florida' : 'Low Voltage Contractor Serving Florida'}
            </h2>
            
            <div className="prose prose-lg max-w-none text-gray-600">
              <p>
                IQES Low Voltage Solutions is a fully licensed and insured contractor providing professional 
                infrastructure services throughout the state of Florida. With over 13 years of experience, 
                we specialize in CCTV security camera installation, fiber optic cabling, structured cabling, 
                access control systems, and network infrastructure.
              </p>
              
              <p>
                Our team of certified technicians serves businesses and organizations in major Florida 
                metropolitan areas including Miami-Dade, Broward, Palm Beach, Orange, Hillsborough, 
                Duval, and Leon counties. Whether you need a single camera installed or a complete 
                building infrastructure overhaul, IQES delivers quality workmanship at competitive prices.
              </p>
              
              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4">Why Florida Businesses Choose IQES</h3>
              
              <ul className="grid md:grid-cols-2 gap-4 list-none pl-0">
                <li className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span><strong>Licensed & Insured:</strong> Full Florida contractor license with comprehensive liability coverage</span>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span><strong>Fast Response:</strong> Local technicians in every major Florida market for quick service</span>
                </li>
                <li className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span><strong>All Business Sizes:</strong> From small offices to large commercial buildings and data centers</span>
                </li>
                <li className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                  <span><strong>Quality Guaranteed:</strong> Industry-leading warranties on all installations</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            {language === 'es' ? '¿Listo para empezar tu proyecto?' : 'Ready to Start Your Project?'}
          </h2>
          <p className="text-white/90 mb-8 max-w-xl mx-auto">
            {language === 'es'
              ? 'Contacta a IQES hoy para una consulta gratuita en tu área.'
              : 'Contact IQES today for a free consultation in your area.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button size="lg" className="bg-white text-orange-600 hover:bg-gray-100 px-8" data-testid="locations-quote-btn">
                {language === 'es' ? 'Solicitar Cotización' : 'Request Quote'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+17869813760">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8" data-testid="locations-call-btn">
                <Phone className="mr-2 w-5 h-5" />
                786-981-3760
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
